/**
 * Cross-tab cache sync · BroadcastChannel · LWW with strict tiebreaker.
 *
 * This is the INBOUND half of cross-tab cache mirroring. The OUTBOUND
 * half is the coalescing queue in `broadcast-queue.ts`, fed from the
 * unified `QueryCache` listener in `SDKProvider`. Both halves share the
 * single `CacheWriteMessage` shape declared here.
 *
 *   - Outbound (provider → queue → channel): every local cache write
 *     (`qc.setQueryData` from a fetch success, optimistic mutation, or
 *     NATS realtime patch) is coalesced by query-key and broadcast as
 *     one `cache:write` message carrying `updatedAt` + `originTabNonce`.
 *   - Inbound (here): peer messages reconcile via LAST-WRITE-WINS:
 *       1. `originTabNonce === ourTabNonce` → drop (our own echo).
 *       2. local `dataUpdatedAt > incoming.updatedAt` → ignore.
 *       3. equal `updatedAt` AND local `tabNonce > incoming` → ignore
 *          (strict `>` keeps same-ms different-tab writes deterministic).
 *       4. accepted canonical-entity projections merge with local fields that
 *          the sender did not observe (for example list row vs detail row).
 *     The accepted write is applied under the `isApplyingRemote` guard so
 *     the outbound listener does NOT re-broadcast it (echo-loop break).
 *
 * In dev, inbound `data` is run through `assertJsonSafe` so a peer
 * sending a `Date`/`Map`/`Set` is caught at the entry, not silently
 * persisted.
 *
 * RN / older Safari: `handle.supported === false` → every method is a
 * no-op and `isApplyingRemote()` is always `false`.
 */

import { mergeNormalizedEntitySnapshot } from '../../core/internal/normalized-entity-write.js';
import { assertJsonSafe } from '../dev-assert.js';
import type { BroadcastHandle } from './broadcast-helper.js';

/** The single cross-tab cache message. Emitted by `broadcast-queue.ts`,
 *  consumed here. `originTabNonce` is both the echo filter and the
 *  LWW tiebreaker value. */
export interface CacheWriteMessage {
  readonly type: 'cache:write';
  readonly queryKey: readonly unknown[];
  readonly data: unknown;
  readonly updatedAt: number;
  readonly originTabNonce: number;
}

/** Minimal QueryClient surface this module touches. */
export interface CacheSyncQc {
  getQueryState(
    key: readonly unknown[],
  ): { readonly dataUpdatedAt: number; readonly data?: unknown } | undefined;
  setQueryData(key: readonly unknown[], data: unknown, options?: { updatedAt?: number }): unknown;
}

export interface OpenCacheBroadcastOptions {
  readonly qc: CacheSyncQc;
  readonly handle: BroadcastHandle;
  readonly tabNonce: number;
  /**
   * Called with the query key immediately BEFORE an accepted inbound
   * write is applied, so the host can attribute the resulting
   * `setQueryData` to `source: 'broadcast'` (devtool write-source +
   * `perf.cacheWrites.byBroadcast`). Injected rather than importing the
   * devtool here so this module stays decoupled from that layer.
   */
  readonly markWrite?: (queryKey: readonly unknown[]) => void;
}

export interface CacheBroadcastBinding {
  /** Post a cache:write message to sibling tabs. */
  post(msg: CacheWriteMessage): void;
  /** Detach the channel listener + close the handle. */
  dispose(): void;
  /**
   * `true` only while an inbound peer write is being applied via
   * `setQueryData`. The provider's outbound cache listener MUST early-out
   * when this is `true`, otherwise applying a remote write would
   * re-broadcast it and amplify across every tab forever.
   */
  isApplyingRemote(): boolean;
  readonly tabNonce: number;
}

function isCacheWriteMessage(v: unknown): v is CacheWriteMessage {
  return typeof v === 'object' && v !== null && (v as { type?: unknown }).type === 'cache:write';
}

function reconcileAcceptedData(
  queryKey: readonly unknown[],
  currentData: unknown,
  incomingData: unknown,
): unknown {
  const isCanonicalEntity = queryKey.length === 3 && queryKey[1] === 'entity';
  if (
    !isCanonicalEntity ||
    incomingData === null ||
    typeof incomingData !== 'object' ||
    Array.isArray(incomingData)
  ) {
    return incomingData;
  }
  return mergeNormalizedEntitySnapshot(currentData, incomingData as Record<string, unknown>);
}

export function openCacheBroadcast(opts: OpenCacheBroadcastOptions): CacheBroadcastBinding {
  const { handle, qc, tabNonce } = opts;
  let applyingRemote = false;

  /**
   * Walk `value` and strip object entries whose value is `undefined`.
   * Returns the same reference when no change is needed (so structural
   * sharing on the receiver stays cheap). Arrays preserve length —
   * an `undefined` slot becomes `null` so JSON.stringify round-trips
   * predictably (matches what JSON.parse(JSON.stringify(arr)) does).
   *
   * Non-plain shapes (Date / Map / Set / RegExp / class instances) are
   * passed through unchanged — the receiver's `assertJsonSafe` will
   * still throw on those, surfacing the real bug.
   */
  function stripUndefinedDeep(input: unknown): unknown {
    if (input === null) return input;
    const t = typeof input;
    if (t !== 'object') return input;
    if (Array.isArray(input)) {
      let changed = false;
      const out: unknown[] = new Array(input.length);
      for (let i = 0; i < input.length; i++) {
        const v = input[i];
        const next = v === undefined ? null : stripUndefinedDeep(v);
        if (next !== v) changed = true;
        out[i] = next;
      }
      return changed ? out : input;
    }
    const proto = Object.getPrototypeOf(input);
    if (proto !== Object.prototype && proto !== null) return input;
    const obj = input as Record<string, unknown>;
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (v === undefined) {
        changed = true;
        continue;
      }
      const next = stripUndefinedDeep(v);
      if (next !== v) changed = true;
      out[k] = next;
    }
    return changed ? out : input;
  }

  if (!handle.supported) {
    return {
      post(): void {},
      dispose(): void {},
      isApplyingRemote: (): boolean => false,
      tabNonce,
    };
  }

  const onMessage = (event: { data: unknown }): void => {
    const received = event.data;
    if (!isCacheWriteMessage(received)) return;
    if (received.originTabNonce === tabNonce) return; // our own echo — drop

    // Dev-mode JSON-safety guard — peer sending Date/Map/Set throws.
    // `undefined` values are stripped at the SEND side now (see `post`
    // below) so the assertion below is a defence-in-depth for the
    // remaining banned shapes (Date / Map / Set / RegExp / BigInt /
    // class instance / function / symbol). A peer using an older
    // build that doesn't strip would still surface here, which is
    // intended — call out the build skew loudly in dev rather than
    // silently corrupting persistence.
    assertJsonSafe(received.data);

    const current = qc.getQueryState(received.queryKey);
    if (current !== undefined) {
      // LWW with STRICT tiebreaker — see module-level rationale.
      if (current.dataUpdatedAt > received.updatedAt) return;
      if (current.dataUpdatedAt === received.updatedAt && tabNonce > received.originTabNonce) {
        return;
      }
    }
    const acceptedData = reconcileAcceptedData(received.queryKey, current?.data, received.data);
    const msg = acceptedData === received.data ? received : { ...received, data: acceptedData };

    applyingRemote = true;
    try {
      opts.markWrite?.(msg.queryKey);
      qc.setQueryData(msg.queryKey, msg.data, { updatedAt: msg.updatedAt });
    } finally {
      applyingRemote = false;
    }
  };

  handle.addEventListener('message', onMessage);

  return {
    post(msg: CacheWriteMessage): void {
      // Strip undefined values from `msg.data` before posting so the
      // peer's dev-assert (`assertJsonSafe`) doesn't trip on
      // "explicit-undefined key" entries. Sources of explicit undefined
      // are diverse — backend handlers that surface optional fields as
      // `undefined` rather than `'0'`/`null`, optimistic patches that
      // spread a partial row, sparse-field codegen output, etc. Pinning
      // each source is a long chase; the boundary fix here makes the
      // wire payload JSON-equivalent by construction (matches what
      // would land if the same payload were persisted via
      // JSON.stringify → JSON.parse), so receivers + persister see a
      // uniform shape. Send-side cost is one walk of the tree per
      // cross-tab broadcast — same complexity as the receiver's
      // dev-assert walk it now replaces.
      const safe = stripUndefinedDeep(msg.data) as typeof msg.data;
      handle.postMessage(safe === msg.data ? msg : { ...msg, data: safe });
    },
    dispose(): void {
      handle.close();
    },
    isApplyingRemote: (): boolean => applyingRemote,
    tabNonce,
  };
}
